import { parseMulti, type FieldShape } from '../lib/custom-field-display';
import { inputClass } from './ui';

/**
 * Entrada de um campo adicional conforme o tipo e a configuração dele.
 * O valor é sempre texto — é como a API guarda (lista = id da opção; múltipla = array JSON).
 */
export function CustomFieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: FieldShape;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const config = field.config ?? {};

  switch (field.type) {
    case 'LONG_TEXT':
    case 'TEXT': {
      const rows = config.rows ?? (field.type === 'LONG_TEXT' ? 3 : 1);
      const maxLength = config.maxLength ?? (field.type === 'LONG_TEXT' ? 5000 : 512);
      return rows > 1 ? (
        <textarea
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          maxLength={maxLength}
          disabled={disabled}
          className={inputClass}
          placeholder={
            config.format && config.format !== 'FREE' ? config.mask || config.format : undefined
          }
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    }
    case 'NUMBER':
      return (
        <input
          type="number"
          step={config.subtype === 'INTEGER' || config.subtype === 'CURRENCY' ? 1 : 'any'}
          min={config.min}
          max={config.max}
          disabled={disabled}
          className={inputClass}
          placeholder={config.subtype === 'CURRENCY' ? 'centavos' : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'DATE': {
      const type =
        config.subtype === 'TIME'
          ? 'time'
          : config.subtype === 'DATETIME'
            ? 'datetime-local'
            : 'date';
      const shown = type === 'datetime-local' && value ? value.slice(0, 16) : value;
      return (
        <input
          type={type}
          disabled={disabled}
          className={inputClass}
          value={shown}
          onChange={(event) =>
            onChange(
              type === 'datetime-local' && event.target.value
                ? new Date(event.target.value).toISOString()
                : event.target.value,
            )
          }
        />
      );
    }
    case 'BOOLEAN':
      return (
        <select
          disabled={disabled}
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">—</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      );
    case 'SELECT':
      return (
        <select
          disabled={disabled}
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Selecione…</option>
          {field.options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      );
    case 'MULTI_SELECT': {
      const selected = parseMulti(value);
      return (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {field.options.map((option) => {
            const active = selected.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  const next = active
                    ? selected.filter((id) => id !== option.id)
                    : [...selected, option.id];
                  onChange(next.length ? JSON.stringify(next) : '');
                }}
                className={`rounded-full border px-2 py-0.5 text-xs ${
                  active ? 'border-transparent text-white' : 'border-slate-300 text-slate-600'
                }`}
                style={active ? { backgroundColor: option.color ?? '#334155' } : undefined}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }
    case 'RATING': {
      const max = config.maxStars ?? 5;
      const current = Number(value) || 0;
      return (
        <div className="mt-1 flex gap-0.5" role="radiogroup" aria-label={field.name}>
          {Array.from({ length: max }, (_, index) => index + 1).map((star) => (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={current === star}
              aria-label={`${star} estrela(s)`}
              disabled={disabled}
              onClick={() => onChange(current === star ? '' : String(star))}
              className={`text-xl leading-none ${star <= current ? 'text-amber-400' : 'text-slate-300'}`}
            >
              ★
            </button>
          ))}
        </div>
      );
    }
    default:
      return (
        <input
          type={
            field.type === 'EMAIL'
              ? 'email'
              : field.type === 'URL'
                ? 'url'
                : field.type === 'PHONE'
                  ? 'tel'
                  : 'text'
          }
          disabled={disabled}
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}
